import React, { useCallback, useEffect } from "react";
import Input from "components/Input";
import Select from "components/Select";
import SubmitButton from "components/SubmitButton";
import { cn } from "utils";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ChildSupportDeductionInputs, childSupportDeductionSchema } from "./const";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createChildSupportDeduction, getChildSupportDeduction, updateChildSupportDeduction } from "services/deductions";
import { getEmployees } from "services/employees";
import { useNavigate, useSearchParams } from "react-router-dom";

const unwrap = (res) => Array.isArray(res) ? res : (res?.data ?? res?.items ?? []);
const toISO = (dateStr) => dateStr ? new Date(dateStr).toISOString() : null;
const toDateInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d)) return '';
    return d.toISOString().split('T')[0];
};

const US_STATES = [
    { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' },
];

const AddEditChildSupportDeduction = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [searchParams] = useSearchParams();
    const editId = searchParams.get('id');
    const isEditMode = !!editId;

    const { data: employeesRaw = [] } = useQuery({
        queryKey: ['employees'],
        queryFn: async () => unwrap(await getEmployees()),
    });

    const { data: deductionData } = useQuery({
        queryKey: ['child-support-deduction', editId],
        queryFn: () => getChildSupportDeduction(editId),
        enabled: isEditMode,
    });

    const employeeList = Array.isArray(employeesRaw) ? employeesRaw : (employeesRaw?.data ?? []);
    const employeeOptions = employeeList.map(e => ({
        value: e.id,
        label: [e.firstName, e.lastName].filter(Boolean).join(' ') || e.id,
    }));

    const { mutate: mutateCreate, isPending: isPendingCreate, isError: isErrorCreate, error: errorCreate } = useMutation({
        mutationFn: createChildSupportDeduction,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['child-support-deductions'] });
            navigate('/deductions');
        },
    });

    const { mutate: mutateUpdate, isPending: isPendingUpdate, isError: isErrorUpdate, error: errorUpdate } = useMutation({
        mutationFn: updateChildSupportDeduction,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['child-support-deductions'] });
            navigate('/deductions');
        },
    });

    const isPending = isEditMode ? isPendingUpdate : isPendingCreate;
    const isError = isEditMode ? isErrorUpdate : isErrorCreate;
    const error = isEditMode ? errorUpdate : errorCreate;

    const { register, handleSubmit, reset, formState: { errors } } = useForm({
        resolver: yupResolver(childSupportDeductionSchema)
    });

    useEffect(() => {
        if (isEditMode && deductionData) {
            const d = deductionData;
            reset({
                [ChildSupportDeductionInputs.ASSIGNED_EMPLOYEE]: d.employeeId || '',
                [ChildSupportDeductionInputs.DESCRIPTION]: d.description || '',
                [ChildSupportDeductionInputs.EFFECTIVE_START]: toDateInput(d.effectiveStart || d.effective),
                [ChildSupportDeductionInputs.EFFECTIVE_END]: toDateInput(d.effectiveEnd),
                [ChildSupportDeductionInputs.AMOUNT]: d.amount != null ? String(d.amount) : '',
                [ChildSupportDeductionInputs.MAX_PERCENT]: d.maxPercent != null ? String(d.maxPercent) : '',
                [ChildSupportDeductionInputs.EXTERNAL_ID]: d.externalId || '',
                [ChildSupportDeductionInputs.FIPS_CODE]: d.fipsCode || '',
                [ChildSupportDeductionInputs.ISSUING_STATE]: d.issuingState || '',
                [ChildSupportDeductionInputs.ISSUE_DATE]: toDateInput(d.issueDate),
                [ChildSupportDeductionInputs.IS_MANAGED]: d.isManaged || false,
            });
        }
    }, [isEditMode, deductionData, reset]);

    const onSubmit = useCallback((values) => {
        const effStart = toISO(values[ChildSupportDeductionInputs.EFFECTIVE_START]);
        const effEnd = toISO(values[ChildSupportDeductionInputs.EFFECTIVE_END]);
        const issDate = toISO(values[ChildSupportDeductionInputs.ISSUE_DATE]);

        // Backend DTO uses non-nullable strings — send "" instead of null to avoid 400
        const payload = {
            employeeId: values[ChildSupportDeductionInputs.ASSIGNED_EMPLOYEE],
            description: values[ChildSupportDeductionInputs.DESCRIPTION] || "",
            amount: parseFloat(values[ChildSupportDeductionInputs.AMOUNT]) || 0,
            maxPercent: parseFloat(values[ChildSupportDeductionInputs.MAX_PERCENT]) || null,
            externalId: values[ChildSupportDeductionInputs.EXTERNAL_ID] || "",
            fipsCode: values[ChildSupportDeductionInputs.FIPS_CODE] || "",
            issuingState: values[ChildSupportDeductionInputs.ISSUING_STATE] || "",
            effectiveStart: effStart || "",
            effectiveEnd: effEnd || "",
            issueDate: issDate || "",
            isManaged: false,
        };
        if (isEditMode) {
            mutateUpdate({ deductionId: editId, ...payload });
        } else {
            mutateCreate(payload);
        }
    }, [isEditMode, editId, mutateCreate, mutateUpdate]);

    return (
        <form className={cn("container-spacing pt-3 pb-5")} onSubmit={handleSubmit(onSubmit)}>
            <h3 className={cn("title mt-2")}>{isEditMode ? 'Edit Child Support Deduction' : 'Add Child Support Deduction'}</h3>
            <div>
                <div>
                    <div className={cn("container-fluid padding-0")}>
                        <div className={cn("row")}>
                            <div className="col-lg-8 col-12">
                                <Select
                                    label="Assigned Employee*"
                                    fullWidth
                                    placeholder="Select employee"
                                    options={employeeOptions}
                                    {...register(ChildSupportDeductionInputs.ASSIGNED_EMPLOYEE)}
                                    error={!!errors[ChildSupportDeductionInputs.ASSIGNED_EMPLOYEE]}
                                    errorText={errors[ChildSupportDeductionInputs.ASSIGNED_EMPLOYEE]?.message}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div>
                    <p className={cn("info-title mt-3")}>General Information</p>
                    <div className={cn("container-fluid padding-0")}>
                        <div className={cn("row")}>
                            <div className="col-lg-8 col-12">
                                <Input label="Description" fullWidth variant="flat"
                                    {...register(ChildSupportDeductionInputs.DESCRIPTION)}
                                    error={!!errors[ChildSupportDeductionInputs.DESCRIPTION]}
                                    errorText={errors[ChildSupportDeductionInputs.DESCRIPTION]?.message}
                                />
                            </div>
                        </div>
                        <div className={cn("row g-3 mt-2")}>
                            <div className="col-lg-4 col-12">
                                <Input label="Start Date" fullWidth variant="flat" type="date"
                                    {...register(ChildSupportDeductionInputs.EFFECTIVE_START)}
                                    error={!!errors[ChildSupportDeductionInputs.EFFECTIVE_START]}
                                    errorText={errors[ChildSupportDeductionInputs.EFFECTIVE_START]?.message}
                                />
                            </div>
                            <div className="col-lg-4 col-12">
                                <Input label="End Date" fullWidth variant="flat" type="date"
                                    {...register(ChildSupportDeductionInputs.EFFECTIVE_END)}
                                    error={!!errors[ChildSupportDeductionInputs.EFFECTIVE_END]}
                                    errorText={errors[ChildSupportDeductionInputs.EFFECTIVE_END]?.message}
                                />
                            </div>
                        </div>
                        <div className={cn("row g-3 mt-2")}>
                            <div className="col-lg-4 col-12">
                                <Input label="Amount* ($)" fullWidth variant="flat" type="number" placeholder="0.00"
                                    {...register(ChildSupportDeductionInputs.AMOUNT)}
                                    error={!!errors[ChildSupportDeductionInputs.AMOUNT]}
                                    errorText={errors[ChildSupportDeductionInputs.AMOUNT]?.message}
                                />
                                <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>Fixed dollar amount withheld per pay period.</div>
                            </div>
                            <div className="col-lg-4 col-12">
                                <Input label="Max Withholding (%)" fullWidth variant="flat" type="number" placeholder="0.00"
                                    {...register(ChildSupportDeductionInputs.MAX_PERCENT)}
                                    error={!!errors[ChildSupportDeductionInputs.MAX_PERCENT]}
                                    errorText={errors[ChildSupportDeductionInputs.MAX_PERCENT]?.message}
                                />
                                <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>Maximum % of disposable income that can be withheld (federal limit is typically 50–65%).</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div>
                    <p className={cn("info-title mt-3")}>Child Support Information</p>
                    <div className={cn("container-fluid padding-0")}>
                        <div className={cn("row g-3 mt-2")}>
                            <div className="col-lg-4 col-12">
                                <Input label="Case # (External ID)" fullWidth variant="flat"
                                    {...register(ChildSupportDeductionInputs.EXTERNAL_ID)}
                                    error={!!errors[ChildSupportDeductionInputs.EXTERNAL_ID]}
                                    errorText={errors[ChildSupportDeductionInputs.EXTERNAL_ID]?.message}
                                />
                            </div>
                            <div className="col-lg-4 col-12">
                                <Input label="FIPS Code" fullWidth variant="flat"
                                    {...register(ChildSupportDeductionInputs.FIPS_CODE)}
                                    error={!!errors[ChildSupportDeductionInputs.FIPS_CODE]}
                                    errorText={errors[ChildSupportDeductionInputs.FIPS_CODE]?.message}
                                />
                                <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>Federal Information Processing Standard code identifying the issuing county/state (provided on the court order).</div>
                            </div>
                        </div>
                        <div className={cn("row g-3 mt-2")}>
                            <div className="col-lg-4 col-12">
                                <Select label="Issuing State" fullWidth placeholder="Select state" options={US_STATES}
                                    {...register(ChildSupportDeductionInputs.ISSUING_STATE)}
                                    error={!!errors[ChildSupportDeductionInputs.ISSUING_STATE]}
                                    errorText={errors[ChildSupportDeductionInputs.ISSUING_STATE]?.message}
                                />
                            </div>
                            <div className="col-lg-4 col-12">
                                <Input label="Issue Date" fullWidth variant="flat" type="date"
                                    {...register(ChildSupportDeductionInputs.ISSUE_DATE)}
                                    error={!!errors[ChildSupportDeductionInputs.ISSUE_DATE]}
                                    errorText={errors[ChildSupportDeductionInputs.ISSUE_DATE]?.message}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="mt-5">
                    {isError && (
                        <p className="text-danger mb-2">
                            {error?.response?.data?.message || error?.response?.data?.title || 'Failed to save deduction. Please try again.'}
                        </p>
                    )}
                    <SubmitButton submitText={isPending ? 'Saving...' : (isEditMode ? 'Update Deduction' : 'Add Deduction')} disabled={isPending} />
                </div>
            </div>
        </form>
    );
}

export default AddEditChildSupportDeduction;

